import type { FinanceHotspot } from '@/mocks/finance';

interface SharePreviewProps {
  hotspot: FinanceHotspot;
  open: boolean;
  onClose: () => void;
}

const tagColorMap: Record<string, string> = {
  '宏观政策': 'bg-blue-50 text-blue-700',
  '央行': 'bg-blue-50 text-blue-700',
  '科技与AI': 'bg-violet-50 text-violet-700',
  'AI应用': 'bg-violet-50 text-violet-700',
  '新能源': 'bg-emerald-50 text-emerald-700',
  '产业链': 'bg-emerald-50 text-emerald-700',
  '消费': 'bg-amber-50 text-amber-700',
  '政策': 'bg-blue-50 text-blue-700',
  '资金流向': 'bg-cyan-50 text-cyan-700',
  '港股与美股': 'bg-indigo-50 text-indigo-700',
  'A股': 'bg-rose-50 text-rose-700',
  '公司动态': 'bg-slate-100 text-slate-700',
};

export default function SharePreview({ hotspot, open, onClose }: SharePreviewProps) {
  if (!open) return null;

  const shareContent = {
    title: hotspot.title,
    summary: hotspot.keyPoints.slice(0, 3).join('；'),
    source: hotspot.source,
    time: hotspot.publishTime,
    tags: hotspot.tags,
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(
        `【个人工作台 · 财经热点】\n${shareContent.title}\n\n${shareContent.summary}\n\n来源：${shareContent.source} | ${shareContent.time}`
      );
      alert('已复制到剪贴板');
    } catch {
      alert('复制失败，请手动复制');
    }
  };

  const handleSaveImage = () => {
    alert('保存图片功能将在后续版本中支持');
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={onClose}
      ></div>

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
        <div className="w-full max-w-sm bg-background-50 rounded-2xl overflow-hidden">
          {/* Share card preview */}
          <div className="p-5 bg-background-50">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-md bg-primary-100 flex items-center justify-center">
                  <i className="ri-funds-line text-primary-600 text-xs"></i>
                </span>
                <span className="text-[11px] font-semibold text-foreground-600">个人工作台 · 财经精选</span>
              </div>
              <span className="text-[10px] text-foreground-400">{shareContent.time}</span>
            </div>

            {/* Title */}
            <h3 className="text-sm font-bold text-foreground-800 leading-snug mb-3 font-heading">
              {shareContent.title}
            </h3>

            {/* Summary points */}
            <div className="space-y-1.5 mb-3">
              {hotspot.keyPoints.slice(0, 3).map((point, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-500 mt-1.5 flex-shrink-0"></span>
                  <span className="text-xs text-foreground-600 leading-relaxed">{point}</span>
                </div>
              ))}
            </div>

            {/* Tags */}
            <div className="flex items-center gap-1.5 flex-wrap mb-3">
              {shareContent.tags.map((tag) => (
                <span
                  key={tag}
                  className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${tagColorMap[tag] || 'bg-secondary-100 text-secondary-600'}`}
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Source */}
            <div className="flex items-center justify-between pt-3 border-t border-background-200/50">
              <span className="text-[10px] text-foreground-400">来源：{shareContent.source}</span>
              <span className="text-[10px] text-foreground-300">个人工作台</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center border-t border-background-200/50">
            <button
              onClick={handleCopyLink}
              className="flex-1 py-3 text-xs font-medium text-foreground-600 flex items-center justify-center gap-1.5 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-link"></i>
              </span>
              复制链接
            </button>
            <div className="w-px h-8 bg-background-200/50"></div>
            <button
              onClick={handleSaveImage}
              className="flex-1 py-3 text-xs font-medium text-foreground-600 flex items-center justify-center gap-1.5 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-image-line"></i>
              </span>
              保存图片
            </button>
            <div className="w-px h-8 bg-background-200/50"></div>
            <button
              onClick={onClose}
              className="flex-1 py-3 text-xs font-medium text-foreground-500 flex items-center justify-center gap-1.5 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              取消
            </button>
          </div>
        </div>
      </div>
    </>
  );
}